import { useTranslate } from '@i18n';
import { Button, Inline, ModalBase, ModalHeader, ModalBody, ModalFooter, Stack, Text, u, Heading3 } from '@ui';

const PUTTER = 'putter';
const UPDATE = 'update';
const CREATE = 'create';

export const SAVE_MODE = {
    PUTTER,
    UPDATE,
    CREATE,
};
export type SaveMode = typeof SAVE_MODE.PUTTER | typeof SAVE_MODE.UPDATE | typeof SAVE_MODE.CREATE | null;

type ClubSaveModalProps = {
    saveMode: SaveMode;
    isCreatePending?: boolean;
    isUpdatePending?: boolean;
    onCreate: () => void;
    onDismiss: () => void;
    onUpdate: () => void;
};

type CreateClubSaveModalProps = Omit<ClubSaveModalProps, 'saveMode' | 'onCreate'> & {
    onCreate: () => void;
};

export const ClubSaveModal = (props: ClubSaveModalProps) => {
    const t = useTranslate();

    return (
        <ModalBase open variant="partial" onDismiss={props.onDismiss}>
            <ModalHeader>{t('tag_selection.update_prompt')}</ModalHeader>
            <RenderModalBody {...props} />
        </ModalBase>
    );
};

const RenderModalBody = (props: ClubSaveModalProps) => {
    const { saveMode, ...rest } = props;

    if (saveMode === PUTTER) {
        return <PutterModal {...rest} />;
    }
    if (saveMode === CREATE) {
        return <ClubModalBodyCurrentActivity {...rest} />;
    }
    return <ClubModalBody {...rest} />;
};

/**
 * Non-current activity save flow.
 *
 * This modal is rendered when:
 * - The user is not on the current activity, or
 * - Creating a new activity is not enabled - i.e. home app.
 *
 * In this case, the user can only update the existing activity
 * with the selected club.
 */
const ClubModalBody = ({ onDismiss, onUpdate, isUpdatePending }: Omit<ClubSaveModalProps, 'saveMode'>) => {
    const t = useTranslate();

    return (
        <>
            <ModalBody>
                <Text variant="p">{t('club_selection_confirm_modal.update')}</Text>
            </ModalBody>
            <ModalFooter
                primary={
                    <Inline justifyContent="flex-end" gap={u(2)}>
                        <Button variant="primary" isLoading={isUpdatePending} onClick={onUpdate}>
                            {t('tag_selection.update_current')}
                        </Button>
                    </Inline>
                }
                secondary={
                    <Button variant="secondary" onClick={onDismiss}>
                        {t('generic_ui.cancel')}
                    </Button>
                }
            />
        </>
    );
};

/**
 * Current activity save flow **(non-putter)**.
 *
 * This modal is rendered when:
 * - The user is on the current activity, and
 * - Creating a new activity is enabled.
 *
 * It gives the user two options:
 * - Update the current activity with the selected club, or
 * - Create a new activity with the selected club and navigate to it.
 *
 */
const ClubModalBodyCurrentActivity = ({
    onDismiss,
    onCreate,
    onUpdate,
    isCreatePending,
    isUpdatePending,
}: CreateClubSaveModalProps) => {
    const t = useTranslate();

    return (
        <>
            <ModalBody>
                <Text variant="p">{t('club_selection_confirm_modal.create_or_update')}</Text>
            </ModalBody>
            <ModalFooter
                primary={
                    <Inline justifyContent="flex-end" gap={u(2)}>
                        <Button variant="primary" isLoading={isCreatePending} onClick={onCreate}>
                            {t('tag_selection.create_new')}
                        </Button>

                        <Button variant="primary" isLoading={isUpdatePending} onClick={onUpdate}>
                            {t('tag_selection.update_current')}
                        </Button>
                    </Inline>
                }
                secondary={
                    <Button variant="secondary" onClick={onDismiss}>
                        {t('generic_ui.cancel')}
                    </Button>
                }
            />
        </>
    );
};

/**
 * Putter-specific save flow.
 *
 * This modal is rendered when:
 * - The user is on the current activity, and
 * - The selected club is a putter, and
 * - Creating a new activity is enabled (kiosk flow).
 *
 * In this case, we do not update the current activity.
 * Instead, we create a new activity and navigate to it.
 */
const PutterModal = ({ onDismiss, onCreate, isCreatePending }: CreateClubSaveModalProps) => {
    const t = useTranslate();
    return (
        <>
            <ModalBody>
                <Stack>
                    <Heading3>{t('club_selection_confirm_modal.putter_heading')}</Heading3>
                    <Text>{t('club_selection_confirm_modal.putter_body')}</Text>
                </Stack>
            </ModalBody>
            <ModalFooter
                primary={
                    <Button variant="primary" isLoading={isCreatePending} onClick={onCreate}>
                        {t('club_selection_confirm_modal.continue_putter')}
                    </Button>
                }
                secondary={
                    <Button variant="secondary" onClick={onDismiss}>
                        {t('generic_ui.cancel')}
                    </Button>
                }
            />
        </>
    );
};
